import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation } from "@angular/core";
import { NgbCollapse, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { Subject } from "rxjs";
import { finalize, first, takeUntil } from "rxjs/operators";
import { Location } from "src/app/model/dispatch.model";
import { PageNumber } from "src/app/model/pagination";
import { RunPlanActivity } from "src/app/model/run";
import { RunTasksService } from "src/app/services/run-tasks.service";
import { makeRunTaskFilter, RunTaskFilterConfig, RunTaskFilterFn, RUN_TASK_FILTER_CONFIG } from "../run-task-filter/run-task-filter";

@Component({
  selector: 'app-run-task-search',
  templateUrl: './run-task-search.component.html',
  styleUrls: ['./run-task-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class RunTaskSearchComponent implements OnInit, OnDestroy, OnChanges {
  @Input('selected')
  public readonly selected: RunPlanActivity[] = [];

  public totalTaskCount = 0;

  public loadingPages = 0;

  public readonly tasksPerPage = 10;

  public isFilterCollapsed = true;

  @ViewChild(NgbCollapse)
  public readonly filterPanel: NgbCollapse;

  public taskFilterConfig = this._taskFilterConfig;

  public get taskFilters() {
    return this._taskFilters;
  }

  public set taskFilters(filters: RunTaskFilterFn[]) {
    this._taskFilters = filters;
    this.filterPanel.toggle(false);
    this._currentPage = -1;
    this.onSetPage({ offset: 0, limit: this.tasksPerPage });
  }

  public set updatedSelection(tasks: readonly RunPlanActivity[]) {
    this._updatedSelection = [...tasks];
  }

  /**
   * Get the list of selected tasks.
   */
  public get updatedSelection() {
    return this._updatedSelection;
  }

  @Output('selectedChange')
  public readonly selectedChange = new EventEmitter<RunPlanActivity[]>();

  /**
   * Get the tasks for display.
   */
  public get tasks() {
    // filter out tasks that do not pass the filters array
    return this._tasks;
  }

  /**
   * Set the tasks to display and mark for re-rendering.
   */
  public set tasks(update: RunPlanActivity[]) {
    this._tasks = update;
    this.cd.markForCheck();
  }

  /**
   * Get the current page.
   */
  public get currentPage() {
    return this._currentPage;
  }

  /**
   * Specify what makes a row unique.
   */
  public get rowIdentity() {
    return (row: RunPlanActivity) => row?.id;
  }

  /**
   * Disable unchecking already checked rows.
   */
  public get displayCheck() {
    return (row: RunPlanActivity) => !row?.actual?.id;
  }

  public constructor(
    public readonly tasksService: RunTasksService,
    private readonly cd: ChangeDetectorRef,
    @Inject(RUN_TASK_FILTER_CONFIG)
    private readonly _taskFilterConfig: RunTaskFilterConfig,
    private toast: ToastrService,
    private modalService: NgbModal
  ) { }

  public ngOnInit() {
    this.onSetPage({ offset: 0, limit: this.tasksPerPage });
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public ngOnChanges(changes: SimpleChanges) {
    for(const prop in changes) {
      if(prop === 'selected') {
        this.updatedSelection = changes[prop].currentValue;
      }
    }
  }

  /**
   * Transform location model into human-readable location.
   * @param loc Location model.
   * @returns Human-readable location.
   */
  public readLocation(loc?: Location) {
    return loc?.short_text ?? [
      loc?.street_no,
      loc?.street,
      loc?.city,
      loc?.suburb,
      `${loc?.state?.abbreviation} ${loc?.postal_code}`
    ]
    .filter((info) => info && info.trim().length)
    .filter((info, index, self) => self.findIndex((lookup) => lookup === info) === index)
    .join(', ');
  }

  /**
   * Update the internal state of selected tasks.
   * @param data.selected Selected tasks.
   */
  public onSelect(data: { selected: RunPlanActivity[] }) {
    this.updatedSelection = data.selected;
    this.selectedChange.emit([...this.updatedSelection]);
  }

  public onSetPage({ offset, limit }) {
    if(offset === this._currentPage) {
      return;
    }

    this._currentPage = offset;

    ++this.loadingPages;

    this.tasksService
      .fetchTasks({
        page: offset + 1,
        per_page: limit,
        include: this.tasksService.fetchOptions.include,
        filters: this.taskFilterConfig
      })
      .pipe(
        first(),
        finalize(() => --this.loadingPages),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe({
        next: (page) => {
          this.totalTaskCount = page.total;
          this.tasks = page.data;
        },
        error: () => {
          this.toast.error('Tasks cannot be fetched at the moment');
        }
      });
  }

  /**
   * Collection of run tasks.
   */
  private _tasks: RunPlanActivity[] = [];

  private _updatedSelection: RunPlanActivity[] = [];

  /**
   * Beacon for unsubscribing to stuff.
   */
  private ngUnsubscribe = new Subject<void>();

  private _currentPage: PageNumber = -1;

  private _taskFilters = makeRunTaskFilter(this._taskFilterConfig);
}
